import { Link } from 'react-router-dom';

import { ListSixInfoDialog } from 'modules/ListSix/components';

import {
  DataTable,
  Paginator,
  SkeletonLoader,
  Button,
  Chip,
  PageActions,
  ExportButton,
  FilterBar,
} from 'components';

import { locales, routes } from 'constants/index';
import { humanReadable } from 'utils';

import { InvoiceInfoDialog } from '../components';

import columns from './columns';
import listSixColumns from './listSixColumns';

import {
  ListFiveOtherInvoicesListingUiProps,
  ListSixTicket,
} from './ListFiveOtherInvoicesListing.types';

function MoreDetailsBodyTemplate(handleShowListFiveRowDetails: any) {
  return function (row: any) {
    return (
      <div
        className="az-tickets__ListFive-details"
        role="presentation"
        onClick={handleShowListFiveRowDetails(row.clover_invoice)}
      >
        Details
      </div>
    );
  };
}

function LinkSixBodyTemplate(row: ListSixTicket) {
  return (
    <Link to={`${routes.listSix.index}/${row.endorsement_id}`}>Read More</Link>
  );
}

function ListSixMoreDetailsBodyTemplate(handleListSixInfoDialogShow: any) {
  return function (row: ListSixTicket) {
    return (
      <div
        className="az-tickets__listFive-details"
        role="presentation"
        onClick={handleListSixInfoDialogShow(row.id)}
      >
        Details
      </div>
    );
  };
}

function DateBodyTemplate(field: 'invoicedate' | 'created' | 'updated') {
  return function (row: ListSixTicket) {
    return humanReadable(row[field]);
  };
}

export default function ListFiveOtherInvoicesListingUi({
  isLoading,
  isListSixLoading,
  isListSixInfoVisible,
  isExporting,
  isInvoiceInfoVisible,
  totalRecords,
  listSixTotalRecords,
  options,
  filter,
  listSixFilter,
  data,
  handlers,
  expandedRows,
}: ListFiveOtherInvoicesListingUiProps) {
  if (!columns[0]?.expander)
    columns.unshift({
      expander: true,
    });

  function listSixTable() {
    return isListSixLoading ? (
      <SkeletonLoader height="10rem" />
    ) : (
      <>
        <DataTable
          className="list-six-table"
          totalRecords={listSixTotalRecords}
          dataKey="id"
          columns={listSixColumns.map((column) => {
            if (column.field === 'invoicedate' || column.field === 'updated') {
              return {
                ...column,
                body: DateBodyTemplate(column.field),
              };
            }

            if (column.field === 'difference_total') {
              return {
                ...column,
                body: (row: any) => {
                  return (
                    <Chip
                      style={{ backgroundColor: row.color_difference }}
                      label={
                        row.difference_total ? row.difference_total : 'N/A'
                      }
                    />
                  );
                },
              };
            }
            if (column.field === 'react_status') {
              return {
                ...column,
                body: (row: any) => {
                  if (row.azadea_rejectiondescription_list6)
                    return row.azadea_rejectiondescription_list6;
                  else if (row.azadea_forceapprovaluser_list6)
                    return 'Approved';
                  else return 'N/A';
                },
              };
            }

            if (column.field === 'details_link') {
              return {
                ...column,
                body: ListSixMoreDetailsBodyTemplate(
                  handlers.handleListSixInfoDialogShow
                ),
              };
            }

            return {
              ...column,
              body: (row: any) =>
                column.field && !row[column.field]
                  ? 'N/A'
                  : column.field && row[column.field],
            };
          })}
          value={data.listSixData}
        />

        {data.listSixData?.length > 0 &&
          (isListSixLoading ? (
            <SkeletonLoader height="3rem" width="40rem" />
          ) : (
            <Paginator
              first={listSixFilter.first}
              rows={listSixFilter.rows}
              totalRecords={listSixTotalRecords}
              rowsPerPageOptions={[10, 50, 100]}
              onPageChange={handlers.handleListSixPageChange}
            />
          ))}
      </>
    );
  }

  return (
    <div className="az-tickets">
      <PageActions>
        <ExportButton
          disabled={isLoading || !data.list || data.list.length === 0}
          onClick={handlers.handleDownload}
          loading={isExporting}
        />
      </PageActions>

      <FilterBar
        loading={isLoading}
        onReset={handlers.handleClearFilter}
        search={{
          inputs: [
            {
              name: 'invoiceNumber',
              placeholder: 'Invoice number',
              value: filter.invoiceNumber || '',
            },
          ],
          onChange: handlers.handleSearchChange,
          onSubmit: handlers.handleSubmitSearch,
          width: 4,
        }}
        filters={[
          {
            kind: 'dropdown',
            name: 'endorsementStatus',
            label: 'Endorsement status',
            placeholder: 'All endorsement statuses',
            value: filter.endorsementStatus || '',
            options: options.endorsementStatuses,
            onChange: handlers.handlerEndorsementStatuses,
          },
          {
            kind: 'dropdown',
            name: 'azadeaStatus',
            label: 'Azadea status',
            placeholder: 'All Azadea statuses',
            value: filter.azadeaStatus || '',
            options: options.azadeaStatuses,
            onChange: handlers.handleAzadeaStatusChange,
          },
        ]}
        advanced={[
          {
            kind: 'dropdown',
            name: 'exceptionDescription',
            label: 'Exception',
            placeholder: 'All exceptions',
            value: filter.exceptionId || '',
            options: options.exceptions,
            onChange: handlers.handleExceptionChange,
          },
          {
            kind: 'dropdown',
            name: 'country',
            label: 'Country',
            placeholder: 'All countries',
            value: filter.country || '',
            options: options.countries,
            onChange: handlers.handleCountryChange,
          },
          {
            kind: 'date',
            name: 'dates',
            label: 'Date range',
            value: filter.dates,
            onChange: handlers.handleDateChange,
          },
        ]}
      />

      {isLoading ? (
        <SkeletonLoader height="10rem" />
      ) : (
        <DataTable
          sortMode="multiple"
          totalRecords={totalRecords}
          expandedRows={expandedRows}
          rowExpansionTemplate={listSixTable}
          onRowExpand={handlers.handleRowExpand}
          onRowCollapse={handlers.handleRowCollapse}
          onRowToggle={handlers.handleRowToggle}
          dataKey="clover_invoice"
          columns={columns.map((column) => {
            if (
              column.field === 'created' ||
              column.field === 'updated' ||
              column.field === 'azadea_forceapprovaldate'
            ) {
              return {
                ...column,
                body: (row: any) =>
                  column.field && !row[column.field]
                    ? 'N/A'
                    : humanReadable(column.field && row[column.field]),
              };
            }

            if (column.field === 'more_details') {
              return {
                ...column,
                body: MoreDetailsBodyTemplate(
                  handlers.handleShowListFiveRowDetails
                ),
              };
            }

            if (column.field === 'list_6_details') {
              return {
                ...column,
                body: LinkSixBodyTemplate,
              };
            }

            if (column.expander) {
              return column;
            }

            return {
              ...column,
              body: (row: any) =>
                column.field && !row[column.field]
                  ? 'N/A'
                  : column.field && row[column.field],
            };
          })}
          value={data.list}
        />
      )}

      {data.list?.length > 0 &&
        (isLoading ? (
          <SkeletonLoader height="3rem" width="40rem" />
        ) : (
          <Paginator
            first={filter.first}
            rows={filter.rows}
            totalRecords={totalRecords}
            rowsPerPageOptions={[10, 50, 100]}
            onPageChange={handlers.handlePageChange}
          />
        ))}

      {data.invoiceInfo && (
        <InvoiceInfoDialog
          invoiceInfo={data.invoiceInfo}
          visible={isInvoiceInfoVisible}
          onHide={handlers.handleHideListFiveRowDetails}
          footer={
            <Button
              className="modal__action modal__action--cancel"
              onClick={handlers.handleHideListFiveRowDetails}
              outlined
              label={locales.general.close}
            />
          }
        />
      )}

      {isListSixInfoVisible && (
        <ListSixInfoDialog
          id={data.listSixSelectedId}
          onClose={handlers.handleListSixInfoDialogClose}
        />
      )}
    </div>
  );
}
